<script setup lang="ts">
import { computed, watch, storeToRefs, useForm, ref } from '#imports';
import { toTypedSchema } from '@vee-validate/valibot';
import { object, pipe, nonEmpty, string, email, optional } from 'valibot';

import { useFeedbackStore } from './../store';
import { useDialogResponseStore } from '@/widgets/dialog-response/store';
import { errorValidationMessages } from '@/app/messages';
import { CaptchaProcessEnum, useCaptchaStore } from '@/features/captcha/store';

const props = withDefaults(defineProps<{
	modelValue: boolean;
}>(), {
	modelValue: false,
});

const emit = defineEmits<{
	(e: 'update:modelValue'): void;
}>();

const value = computed({
	get: () => props.modelValue,
	set: () => emit('update:modelValue'),
});

const feedbackStore = useFeedbackStore();
const { selection } = storeToRefs(feedbackStore);
const { handleResetSelection, sendFeedback } = feedbackStore;

const dialogResponseStore = useDialogResponseStore();
const { setResponseModalMessage, showResponseModal } = dialogResponseStore;

/** Form Validation */
const { defineField, handleSubmit, handleReset, errors } = useForm({
	validationSchema: toTypedSchema(
		object({
			text: optional(pipe(
				string(),
				nonEmpty(errorValidationMessages.required.feedback),
			), ''),
			email: optional(pipe(
				string(),
				nonEmpty(errorValidationMessages.required.email),
				email(),
			), ''),
		}),
	),
});

const [textModel, textAttrs] = defineField('text');
const [emailModel, emailAttrs] = defineField('email');

const isLoading = ref(false);

/** Handle Submit */
const captchaStore = useCaptchaStore();
const { captchaToken, captchaProcess } = storeToRefs(captchaStore);
const { captchaExecute, captchaReset } = captchaStore;

const payload = ref();

const onSubmit = handleSubmit((model) => {
	payload.value = model;
	captchaExecute(CaptchaProcessEnum.Feedback);
});

watch(captchaToken, async (val) => {
	if (val.length && captchaProcess.value === CaptchaProcessEnum.Feedback) {
		isLoading.value = true;

		await sendFeedback({
			...payload.value,
			captcha_token: val,
		});

		setResponseModalMessage('Ваш отзыв отправлен');
		isLoading.value = false;
		captchaReset();

		setTimeout(() => {
			showResponseModal();
		}, 0);

		emit('update:modelValue');
	}
});

/** Обнуление формы после закрытия */
watch(value, (val) => {
	if (!val) {
		handleResetSelection();
		handleReset();
	} else if (val && selection.value.length) {
		textModel.value = `"${selection.value}" - `;
	}
});
</script>

<template>
	<v-dialog v-model="value">
		<form class="form" @submit.prevent="onSubmit">
			<div class="form__block">
				<h2 class="form__title">
					Форма обратной связи
				</h2>
			</div>

			<div class="form__block">
				<div class="form__field">
					<v-input
						v-model="textModel"
						v-bind="textAttrs"
						tag="textarea"
						label="Отзыв"
						required
						:is-error="!!errors?.text"
						:error-message="errors?.text"
					/>
				</div>
			</div>

			<div class="form__block">
				<div class="form__field">
					<v-input
						v-model="emailModel"
						v-bind="emailAttrs"
						type="email"
						label="E-mail"
						required
						description="Ваш E-mail для обратной связи"
						:is-error="!!errors?.email"
						:error-message="errors?.email"
					/>
				</div>
			</div>

			<div class="form__block">
				<v-button
					type="submit"
					:disabled="isLoading"
					class="form__submit"
				>
					Отправить
				</v-button>
			</div>
		</form>
	</v-dialog>
</template>
